import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Flag, ShieldAlert, Image as ImageIcon, X } from "lucide-react";
import { useT } from "../context/LanguageContext";

/**
 * Players-tab augmentation: a report button next to each player in a league.
 * Uploads to /leagues/{lid}/players/{pid}/report as multipart/form-data with
 * a MANDATORY screenshot (anti-abuse evidence). Shows the running report
 * count vs the 10-report auto-ban threshold.
 */
export default function ReportPlayerButton({ leagueId, playerId, playerName, myId }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/leagues/${leagueId}/reports`);
      const s = (data.summary || []).find((r) => r.reported_id === playerId);
      setSummary(s || { unique_reporter_count: 0, threshold: 10, mine: false });
    } catch { /* noop */ }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [leagueId, playerId]);

  // Revoke blob preview to avoid memory leaks.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (playerId === myId) return null; // can't report self

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error(t("report.fileTooBig"));
      e.target.value = "";
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error(t("report.fileBadType"));
      e.target.value = "";
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) return;
    if (!file) {
      toast.error(t("report.screenshotRequired"));
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reason", reason.trim());
      fd.append("screenshot", file);
      const { data } = await api.post(
        `/leagues/${leagueId}/players/${playerId}/report`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (data.banned) {
        toast.success(t("report.toastBanned", { name: playerName, threshold: data.threshold }));
      } else {
        toast.success(t("report.toastSubmitted", { count: data.report_count, threshold: data.threshold }));
      }
      setReason("");
      clearFile();
      setOpen(false);
      refresh();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const count = summary?.unique_reporter_count || 0;
  const threshold = summary?.threshold || 10;
  const mine = summary?.mine;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`report-btn-${playerId}`}
        className={`inline-flex items-center gap-1.5 border font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 transition-colors ${
          mine ? "border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-[#09090B]"
               : "border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:border-[#E11D48] hover:text-[#E11D48]"
        }`}
        title={mine ? t("report.alreadyReported") : t("report.reportName", { name: playerName })}
      >
        <Flag className="w-3 h-3" />
        {count > 0 ? `${count}/${threshold}` : t("report.btn")}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-[300px] bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E11D48]/60 p-3 z-40 shadow-[0_10px_30px_rgba(0,0,0,0.6)]" data-testid={`report-form-${playerId}`}>
          <div className="flex items-start gap-2 mb-3">
            <ShieldAlert className="w-4 h-4 text-[#E11D48] mt-0.5 flex-shrink-0" />
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E11D48] leading-relaxed">
              {t("report.intro", { name: playerName, threshold })}
            </div>
          </div>
          <form onSubmit={submit}>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid={`report-reason-${playerId}`}
              placeholder={t("report.reasonPh")}
              minLength={5}
              maxLength={500}
              rows={3}
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1.5 text-xs font-body focus:border-[#E11D48] outline-none text-[#09090B] dark:text-[#F4F4F5] resize-none"
              required
            />

            {/* Mandatory screenshot uploader */}
            <div className="mt-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E11D48] flex items-center gap-1.5 mb-1.5" htmlFor={`report-screenshot-${playerId}`}>
                <ImageIcon className="w-3 h-3" /> {t("report.screenshotRequiredLabel")}
              </label>
              {previewUrl ? (
                <div className="relative inline-block border border-[#E4E4E7] dark:border-[#2A2A30]" data-testid={`report-screenshot-preview-${playerId}`}>
                  <img src={previewUrl} alt="evidence preview" className="max-h-24 w-auto block" />
                  <button
                    type="button"
                    onClick={clearFile}
                    aria-label="Remove screenshot"
                    className="absolute top-0.5 right-0.5 bg-black/70 text-white p-0.5 hover:bg-[#E11D48] transition-colors"
                    data-testid={`report-screenshot-clear-${playerId}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <input
                  id={`report-screenshot-${playerId}`}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={onPickFile}
                  data-testid={`report-screenshot-input-${playerId}`}
                  className="block w-full text-[10px] font-mono uppercase tracking-[0.2em] text-[#71717A] file:bg-[#7C3AED] file:text-white file:border-0 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em] file:px-2 file:py-1 file:mr-2 hover:file:bg-[#6D28D9] cursor-pointer"
                  required
                />
              )}
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#71717A] mt-1">
                {t("report.screenshotMeta")}
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 mt-3">
              <button
                type="button"
                onClick={() => { setOpen(false); clearFile(); setReason(""); }}
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] px-2 py-1"
              >
                {t("report.cancel")}
              </button>
              <button
                type="submit"
                disabled={busy || reason.trim().length < 5 || !file}
                data-testid={`report-submit-${playerId}`}
                className="bg-[#E11D48] text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-40 hover:bg-[#F43F5E] transition-colors"
              >
                {busy ? t("report.submitting") : (mine ? t("report.update") : t("report.submit"))}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
